import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useUser } from '../../../context/UserContext';
import {
  CircularProgress,
  FormHelperText,
  Stack,
  FormControl,
  MenuItem,
  TextField,
} from '@mui/material';
import useForm from '../../../pages/AdminContentMgt/Components/useForm';
import { useQuery } from '@tanstack/react-query';
import { GetPlanType, GetProducts } from '../../../api/proposals';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

const taxStatus = [
  { title: 'QUALIFIED', value: 'QUALIFIED' },
  { title: 'NON QUALIFIED', value: 'NONQUALIFIED' },
];

const ProductInfo = forwardRef((props, ref) => {
  const { versions, data } = props;
  const [taxStatusOption, setTaxStatusOption] = useState();
  const user = useUser();

  const product = useQuery({
    queryKey: ['GetProducts'],
    queryFn: async () => await GetProducts()(),
  });

  const planTypeQuery = useQuery({
    queryKey: ['ProductOptions', data.product],
    queryFn: async () => await GetPlanType(data.product)(),
    enabled: !!data.product,
  });

  const { isPending: isLoading, isSuccess, isError, data: planTypeData } = planTypeQuery;

  const validate = (fieldValues = values) => {
    let temp = { ...errors };
    if ('planType' in fieldValues)
      temp.planType = fieldValues.planType ? '' : 'Plan Type is required.';
    if ('taxStatus' in fieldValues)
      temp.taxStatus = fieldValues.taxStatus ? '' : 'Tax Status is required.';
    if ('singlePremium' in fieldValues)
      temp.singlePremium =
        isNaN(parseFloat(fieldValues.singlePremium.replace(/,/g, ''))) ||
        parseFloat(fieldValues.singlePremium.replace(/,/g, '')) < 5000
          ? 'Single Premium Amount should be minimum 5000'
          : '';

    setErrors({
      ...temp,
    });
    if (fieldValues == values) return Object.values(temp).every((x) => x == '');
  };

  const { values, errors, setErrors, handleInputChange, handleIntChange, handleNoEventDateChange } =
    useForm(
      {
        planType: versions?.[0]?.illustrationData?.planType || '',
        taxStatus: versions?.[0]?.illustrationData?.taxStatus || '',
        singlePremium: versions?.[0]?.illustrationData?.amount?.toLocaleString('en-US') || '',
        effectiveDate: versions?.[0]?.illustrationData?.effectiveDate || new Date().toISOString(),
      },
      true,
      validate
    );

  const getClientInfo = () => {
    if (validate()) {
      const currentVersion = data.createdVersions[0];
      currentVersion.illustrationData = {
        ...currentVersion.illustrationData,
        planType: values.planType,
        taxStatus: values.taxStatus,
        amount: parseFloat(values.singlePremium.replace(/,/g, '')),
        effectiveDate: values.effectiveDate,
      };
      data.createdVersions = [currentVersion];
      return data;
    }
  };

  useImperativeHandle(ref, () => {
    return {
      getClientInfo: () => getClientInfo(),
    };
  }, [validate]);

  return (
    <Stack>
      <Stack mb={2} spacing={2}>
        {user.isHO && (
          <>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Effective Date"
                name="effectiveDate"
                // 👇 if values.effectiveDate is missing, default to today
                value={
                  values.effectiveDate ? dayjs.utc(values.effectiveDate) : dayjs() // today in local time
                }
                size="small"
                variant="standard"
                onChange={(newVal) => {
                  if (newVal && dayjs(newVal).isValid()) {
                    const isoUTC = dayjs(newVal).utc().toISOString();
                    handleNoEventDateChange(isoUTC, 'effectiveDate');
                  } else {
                    handleNoEventDateChange(null, 'effectiveDate');
                  }
                }}
                slotProps={{
                  textField: { variant: 'standard' },
                }}
              />
            </LocalizationProvider>
            <FormHelperText className="color-error">
              {errors.effectiveDate !== undefined && errors.effectiveDate !== ''
                ? 'Effective Date is required'
                : ''}
            </FormHelperText>
          </>
        )}
        <FormControl fullWidth>
          <TextField
            select={true}
            label="Plan Type"
            variant="standard"
            name="planType"
            value={values.planType}
            onChange={handleInputChange}
            error={errors.planType !== undefined && errors.planType !== ''}
            required
          >
            {isLoading && (
              <MenuItem>
                <CircularProgress />
              </MenuItem>
            )}
            <MenuItem value="">Select Plan Type</MenuItem>
            {planTypeData?.map((item, index) => (
              <MenuItem value={item.value} key={`${item.text}-${index}`}>
                {item.text}
              </MenuItem>
            ))}
          </TextField>
          <FormHelperText className="color-error">{errors.planType}</FormHelperText>
        </FormControl>
        <TextField
          name="singlePremium"
          variant="standard"
          value={values.singlePremium}
          onChange={handleInputChange}
          error={errors.singlePremium !== undefined && errors.singlePremium !== ''}
          helperText={errors.singlePremium}
          label="Single Premium ($)"
          fullWidth
        />
      </Stack>
      <Stack mb={2} spacing={2}>
        <FormControl fullWidth>
          <TextField
            select={true}
            name="taxStatus"
            label="Tax Status"
            variant="standard"
            value={values.taxStatus}
            onChange={handleInputChange}
            error={errors.taxStatus !== undefined && errors.taxStatus !== ''}
            required
          >
            <MenuItem value="">Select Tax Status</MenuItem>
            {taxStatus.map((item, index) => (
              <MenuItem value={item.value} key={index}>
                {item.title}
              </MenuItem>
            ))}
          </TextField>
          <FormHelperText className="color-error">{errors.taxStatus}</FormHelperText>
        </FormControl>
      </Stack>
    </Stack>
  );
});

export default ProductInfo;
